import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryA from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryB from "@/assets/sprinter-limo-2.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryC from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/car-service-atlanta-to-savannah";
const PAGE_TITLE = "Atlanta to Savannah Car Service | Private Sprinter Transfer";
const PAGE_DESC =
  "Private chauffeured Mercedes-Benz Sprinter transfers from Atlanta to Savannah and Tybee Island. Door to door, fixed rate, one vehicle the whole way. Call (678) 431-7222.";

const faqs = [
  {
    q: "How long is the drive from Atlanta to Savannah?",
    a: "Roughly 250 miles and four to four and a half hours door to door on I-16, plus one comfort stop. We build in an extra 30 minutes for Friday afternoon departures out of the city, when I-75 and I-16 both slow near Macon.",
  },
  {
    q: "Is it billed by the hour or as a flat transfer?",
    a: "Long-distance runs like this are quoted as a fixed point-to-point rate confirmed in writing, including the chauffeur's return leg. Hourly pricing only makes sense if you want the vehicle and chauffeur to stay with you in Savannah for the weekend.",
  },
  {
    q: "Can the chauffeur stay with us in Savannah?",
    a: "Yes. Some clients keep the Sprinter and chauffeur on an as-directed daily basis for Historic District dinners, Tybee Island beach days, and the return leg. We arrange the chauffeur's lodging and quote it into the daily rate.",
  },
  {
    q: "How much luggage fits for a weekend group?",
    a: "The Executive Sprinter handles eight passengers with full-size cases and golf bags without stacking anything in the cabin. For a wedding party moving garment bags and gifts, we recommend the Sprinter Limousine or a second vehicle rather than compressing the load.",
  },
  {
    q: "Do you make stops on the way?",
    a: "One scheduled comfort stop is standard, usually around Dublin or the Pooler exits. Additional stops, a lunch in Macon, a detour to Statesboro, are fine and simply noted on the quote so the timing is honest.",
  },
  {
    q: "Can you collect us at Savannah/Hilton Head Airport instead?",
    a: "Yes. We regularly run the reverse pattern: fly into SAV, meet a chauffeur inside the terminal, then travel to Sea Island, Jekyll Island, or back to Atlanta by road when weather grounds a connecting flight.",
  },
];

export const Route = createFileRoute("/car-service-atlanta-to-savannah")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "atlanta to savannah car service, private transfer atlanta to savannah, savannah sprinter transfer, chauffeur atlanta to savannah, tybee island car service from atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Atlanta to Savannah Car Service",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Long Distance Private Car Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Savannah" },
              { type: "City", name: "Tybee Island" },
              { type: "City", name: "Macon" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Atlanta • I-16 • Savannah"
      h1="Private Car Service from Atlanta to Savannah"
      intro="Four hours to the coast can be time to rest. I'll arrange your door-to-door trip from Atlanta to Savannah's Historic District, Tybee Island or the Golden Isles."
      heroImage={heroImage}
      heroAlt="Executive Mercedes-Benz Sprinter cabin prepared for a long-distance transfer from Atlanta to Savannah"
      midImage={midImage}
      midAlt="Lounge seating inside a Mercedes-Benz Sprinter used for Atlanta to Savannah transfers"
      sectionOneTitle="Arrive Ready for the Coast"
      sectionOneBody={[
        "You may have dinner on Bay Street, a wedding or a family reunion ahead. My chauffeur handles I-16 while you rest, work or spend time together.",
        "The Sprinter has power at every seat and space for documents, luggage and golf clubs. Executive offsites and Sea Island golf groups use the route too.",
        "I'll confirm your fixed point-to-point rate in writing, including the return leg.",
      ]}
      bulletsTitle="What the Savannah Transfer Includes"
      bulletsIntro="One vehicle and chauffeur from your Atlanta pickup to the coast."
      bullets={[
        "Fixed point-to-point rate confirmed in writing, return leg included",
        "Pickup from any Atlanta, Buckhead, Alpharetta, or Sandy Springs address",
        "One scheduled comfort stop; extra stops quoted honestly up front",
        "Power at every seat and room to work or sleep across four hours",
        "Full luggage capacity, cases, garment bags, and golf clubs without stacking",
        "Chauffeur available on an as-directed daily basis once you arrive",
        "Reverse runs from Savannah/Hilton Head Airport (SAV) back to Atlanta",
      ]}
      coverageTitle="Where This Route Ends"
      coverageIntro="Coastal Georgia and Lowcountry destinations we run from Atlanta on a regular basis."
      coverage={[
        "Savannah Historic District",
        "River Street & Plant Riverside",
        "Tybee Island",
        "Savannah/Hilton Head Airport (SAV)",
        "Hilton Head Island, SC",
        "Bluffton & Palmetto Bluff, SC",
        "Sea Island & St. Simons",
        "Jekyll Island",
        "Brunswick",
        "Statesboro",
        "Macon & Dublin stopovers",
        "Pooler & Richmond Hill",
      ]}
      routeTable={{
        title: "Atlanta to the Coast, Distance and Drive Time",
        intro:
          "Real timings our chauffeurs plan against, measured from in-town Atlanta on a normal weekday departure.",
        rows: [
          {
            route: "Atlanta → Savannah Historic District",
            distance: "~250 mi",
            time: "4h 00m – 4h 45m",
            notes: "I-75 S to I-16 E. Add 30 min for Friday afternoon departures.",
          },
          {
            route: "Atlanta → Tybee Island",
            distance: "~268 mi",
            time: "4h 30m – 5h 15m",
            notes: "Islands Expressway after Savannah; summer weekend bridge traffic is the variable.",
          },
          {
            route: "Atlanta → Sea Island / St. Simons",
            distance: "~300 mi",
            time: "4h 45m – 5h 30m",
            notes: "I-75 S to I-16 E to I-95 S. Gate access at Sea Island arranged in advance.",
          },
          {
            route: "Atlanta → Jekyll Island",
            distance: "~295 mi",
            time: "4h 45m – 5h 30m",
            notes: "Causeway parking fee handled by the chauffeur, itemized on the invoice.",
          },
          {
            route: "Atlanta → Hilton Head Island, SC",
            distance: "~290 mi",
            time: "4h 45m – 5h 30m",
            notes: "I-95 N past Savannah. Gated plantation access needs the guest name in advance.",
          },
          {
            route: "SAV Airport → Atlanta",
            distance: "~245 mi",
            time: "4h 00m – 4h 45m",
            notes: "Common when a connection is cancelled; chauffeur meets you inside the terminal.",
          },
          {
            route: "Savannah as-directed day",
            distance: "Varies",
            time: "6–10 hr block",
            notes: "Vehicle and chauffeur stay with the group for dinners, tours, and beach runs.",
          },
        ],
        footnote:
          "Every long-distance quote includes the chauffeur's deadhead return, so the number you approve is the number you pay.",
      }}
      detailSections={[
        {
          title: "Who Books the Savannah Run",
          list: [
            "Wedding parties travelling to Historic District and Lowcountry venues together.",
            "Golf groups heading to Sea Island, Jekyll, and the Lowcountry courses.",
            "Executive teams running an offsite on the coast who want the drive to be working time.",
            "Families avoiding two rental cars and a split convoy.",
            "Travellers rerouted by a cancelled SAV or ATL connection who still need to arrive tonight.",
          ],
        },
        {
          title: "How We Plan a Long-Distance Departure",
          body: [
            "We set the departure time backwards from your first commitment on the coast, a rehearsal dinner, a tee time, a board session, and add a buffer for the Macon interchange, which is the only reliably slow point on the route.",
            "Your chauffeur confirms by text the evening before with the vehicle, plate, and pickup time, then again on departure morning. Luggage is loaded before passengers board, so nobody stands in a driveway.",
          ],
        },
        {
          title: "Choosing the Vehicle for a Four-Hour Drive",
          list: [
            "Up to 6 passengers (captain seats) or 7 (bench seats) with luggage, Suburban SUV, the simplest and quietest option.",
            "Up to 13 passengers, Executive Sprinter with facing captain's chairs and full luggage capacity.",
            "9–14 passengers, Mercedes-Benz Sprinter Limousine with lounge seating and wet bar.",
            "15+ passengers, two Sprinters travelling together under one lead chauffeur.",
          ],
        },
      ]}
      gallery={{
        title: "Vehicles Used on the Savannah Route",
        intro: "Cabins built for four hours, not forty minutes.",
        images: [
          { src: galleryA, alt: "Executive Mercedes-Benz Sprinter interior configured for long-distance travel", caption: "Executive Sprinter, up to 13" },
          { src: galleryB, alt: "Mercedes-Benz Sprinter Limousine lounge cabin for group coastal trips", caption: "Sprinter Limousine, up to 14" },
          { src: galleryC, alt: "Suburban SUV interior for small-group Atlanta to Savannah transfers", caption: "Suburban SUV, up to 6 (captain seats) or 7 (bench seats)" },
        ],
      }}
      faqs={faqs}
      faqTitle="Atlanta to Savannah Car Service FAQs"
      relatedTitle="Related Destination Services"
      related={[
        { to: "/car-service-atlanta-to-chattanooga", title: "Atlanta to Chattanooga", desc: "Private transfers north through the ridge country." },
        { to: "/car-service-atlanta-to-lake-oconee", title: "Atlanta to Lake Oconee", desc: "Reynolds and Ritz-Carlton weekends, door to door." },
        { to: "/luxury-car-service-atlanta", title: "Luxury Car Service Atlanta", desc: "Private chauffeured travel across metro Atlanta." },
      ]}
      ctaTitle="Text Me Your Coastal Trip Plans"
      ctaBody="Text me your date, pickup, destination and group size, plus luggage, clubs and any stops. I'll send your quote in writing."
    />
  );
}
